import { inject, Injectable } from '@angular/core';
import { FooterButtonType } from '../interfaces';
import { Message, MessageKind } from '@clientwire/api';
import { ActiveConversationStore, MessageStore } from '../stores';
import { SharedUtils } from '../utils';
import { throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivityService } from './activity.service';
import { ConversationHelperService } from './conversation-helper.service';
import { TranslateService } from '@ngx-translate/core';
import { logger } from '../utils';
import { FilesToUpload } from '@akostm/orbit-ui';
import imageCompression from 'browser-image-compression';

@Injectable({
  providedIn: 'root',
})
export class MessageService {
  readonly #http = inject(HttpClient);
  readonly #conversationMessageStore = inject(MessageStore);
  readonly #conversationHelperService = inject(ConversationHelperService);
  readonly #activeConversationStore = inject(ActiveConversationStore);
  readonly #activityService = inject(ActivityService);
  readonly #allowedFileTypes: MessageKind[] = ['IMAGE', 'VIDEO', 'AUDIO'];
  readonly #translateService = inject(TranslateService);

  async sendMessage(event: FooterButtonType, conversationId: string) {
    switch (event) {
      case FooterButtonType.Text: {
        const message =
          this.#activeConversationStore.entityMap()[conversationId]?.messageInput.value;
        const messageSourceId =
          this.#activeConversationStore.entityMap()[conversationId]?.messageSourceId;
        const files = this.#activeConversationStore.entityMap()[conversationId]?.files;
        if (files && files.length > 0) {
          files.forEach((file) => {
            const fileType = file.file?.type?.split('/')[0]?.toUpperCase();
            if (fileType && this.#allowedFileTypes.includes(fileType as MessageKind)) {
              this.#conversationHelperService
                .createNewMessage(
                  conversationId,
                  fileType as MessageKind,
                  messageSourceId,
                  message ?? '',
                  undefined,
                  undefined,
                  file.src
                )
                .then((res: any) => {
                  if (res) {
                    const response = res as Message;
                    if (response.asset?.uploadUrl) {
                      this.onUpload(conversationId, response.asset.uploadUrl, files, response);
                    }
                    this.setLastUnreadMessage(conversationId);
                  }
                });
              this.#activeConversationStore.setMessageInputValue(conversationId, '');
              this.#activeConversationStore.setNewMessageSourceId(conversationId);
              this.#activeConversationStore.updateConversation(conversationId, {
                files: [],
              });
            } else {
              this.#activeConversationStore.updateConversation(conversationId, {
                messageSending: false,
              });
            }
          });
        } else {
          if (message) {
            this.#conversationHelperService.createNewMessage(
              conversationId,
              'TEXT',
              messageSourceId,
              message
            );
          }
          this.#activeConversationStore.setMessageInputValue(conversationId, '');
          this.#activeConversationStore.setNewMessageSourceId(conversationId);
        }
        break;
      }

      default: {
        logger.debug(event);
        break;
      }
    }
  }

  async onUpload(
    conversationId: string,
    url: string,
    files: FilesToUpload[] | string[],
    message: Message
  ) {
    let file: Blob | string | undefined;
    if (files && files.length > 0) {
      const tmpfile = files[0];
      if (tmpfile) {
        if (typeof tmpfile === 'string') {
          file = SharedUtils.dataURIToBlob(tmpfile);
        } else {
          file = SharedUtils.dataURIToBlob(tmpfile.src);
        }
      }
      if (!file)
        throw new Error(this.#translateService.instant('SHARED.SERVICES.MESSAGE.FILE_NOT_FOUND'));

      // Process image compression for image files
      let processedFile: Blob | string = file;
      if (message.kind === 'IMAGE' && file instanceof Blob) {
        try {
          processedFile = await this.compressImage(file);
        } catch (error) {
          logger.error('Image compression failed, using original file:', error);
          // Fallback to original file if compression fails
          processedFile = file;
        }
      }

      let headers = new HttpHeaders();
      if (processedFile instanceof Blob && processedFile.type) {
        headers = headers.set('Content-Type', processedFile.type);
      }

      const upload$ = this.#http.put(url, processedFile, { headers, observe: 'response' });
      upload$.subscribe({
        next: (res: any) => {
          // Check the response status from the full HTTP response
          if (res.status >= 200 && res.status < 300) {
            this.#activeConversationStore.updateConversation(conversationId, {
              messageSending: false,
            });
          }
        },
        error: (error: any) => {
          this.#conversationMessageStore.addMessageIdToErrors(message, error);
          this.#activeConversationStore.updateConversation(conversationId, {
            messageSending: false,
          });
          return throwError(() => error);
        },
      });
    }
  }

  /**
   * Compresses an image using browser-image-compression
   * @param file - The image file to compress
   * @returns Promise<Blob> - The compressed image as a Blob
   */
  private async compressImage(file: Blob): Promise<Blob> {
    try {
      const options = {
        maxSizeMB: 1, // Maximum file size in MB
        maxWidthOrHeight: 1024, // Maximum width or height
        useWebWorker: true, // Use web worker for better performance

        initialQuality: 0.8, // Initial quality (0.1 - 1.0)
      };

      const compressedFile = await imageCompression(file as File, options);
      return compressedFile;
    } catch (error) {
      logger.error('Browser image compression error:', error);
      throw error;
    }
  }

  async setLastUnreadMessage(conversationId: string, message?: Message) {
    let latestMessage = message;
    if (!latestMessage) {
      const conversation = this.#conversationMessageStore.entityMap()[conversationId];
      latestMessage = conversation?.messages[0];
    }

    if (latestMessage) {
      await this.#activityService.handleMessageSeenActivity(latestMessage);
    }
  }

  async setUnreadMessage(message: Message) {
    await this.#activityService.handleMessageSeenActivity(message);
  }

  resendMessage(conversationId: string, messageId: string) {
    console.log('resendMessage', conversationId, messageId);
    const message = this.#conversationMessageStore
      .entityMap()
      [conversationId]?.messages?.find((message) => message.id === messageId);
    if (message) {
      // Check if the message id is a local message or a server message
      if (messageId.startsWith('new-')) {
        // remove messageid from messageIdsWithError
        this.#conversationMessageStore.removeMessageIdFromErrors(conversationId, messageId);
        // Send again to the server
        this.#conversationMessageStore.sendLocalMessageToServer(message);
      } else {
        // The message already exists on the server probably an assets upload error
        // Try re-upload the assets
        // remove messageid from messageIdsWithError
        this.#conversationMessageStore.removeMessageIdFromErrors(conversationId, messageId);
        // Send again to the server
        const uploadUrl = message.asset?.uploadUrl;
        const file = message.asset?.downloadUrl;
        if (file && uploadUrl) {
          this.onUpload(conversationId, uploadUrl, [file], message);
        }
      }
    }
  }
}
